import { toast } from "react-toastify";
import { Session } from "../features/sessions/models/Session";
import { mapRecurrenceTypeToFrequency } from "../features/sessions/utils/RecurrenceTypeMapper";

export interface FormErrors {
  [key: string]: string | undefined;
}

/** A t-function shape compatible with react-i18next without importing its types here. */
type Translate = (key: string, defaultValue: string, options?: Record<string, unknown>) => string;

const fallbackT: Translate = (_key, defaultValue, options) =>
  options ? defaultValue.replace("{{count}}", String(options.count)) : defaultValue;

export interface ValidationErrorMessages {
  eventName?: string;
  eventDate?: string;
  startTime?: string;
  duration?: string;
  frequency?: string;
  eventNotes?: string;
}

/**
 * Shows a user-friendly toast notification for validation errors
 * @param errors - Object containing validation errors
 * @param t - Translation function (optional; falls back to English)
 */
export const showValidationErrorNotification = (errors: FormErrors, t: Translate = fallbackT): void => {
  const errorCount = Object.keys(errors).length;
  const errorMessage =
    errorCount === 1
      ? t("validation.fixOne", "Please fix the validation error to continue.")
      : t("validation.fixMany", "Please fix the {{count}} validation errors to continue.", {
          count: errorCount,
        });

  toast.error(errorMessage, {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
};

/**
 * Validates time format (HH:MM or HH:MM:SS in 24-hour format)
 * @param time - Time string to validate
 * @returns true if valid, false otherwise
 */
export const isValidTimeFormat = (time: string): boolean => {
  if (!time) return false;

  // Check if it matches HH:MM or HH:MM:SS format
  const timeRegexHHMM = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
  const timeRegexHHMMSS = /^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;

  if (!timeRegexHHMM.test(time) && !timeRegexHHMMSS.test(time)) return false;

  // Additional validation: ensure hours and minutes (and seconds if present) are valid
  const timeParts = time.split(":").map(Number);
  const [hours, minutes, seconds] = timeParts;

  const isValidHours = hours >= 0 && hours <= 23;
  const isValidMinutes = minutes >= 0 && minutes <= 59;
  const isValidSeconds = seconds === undefined || (seconds >= 0 && seconds <= 59);

  return isValidHours && isValidMinutes && isValidSeconds;
};

/**
 * Validates duration (must be a positive integer)
 * @param duration - Duration string to validate
 * @returns true if valid, false otherwise
 */
export const isValidDuration = (duration: string | number): boolean => {
  if (!duration) return false;

  const durationNum =
    typeof duration === "string" ? parseInt(duration, 10) : duration;
  return !isNaN(durationNum) && durationNum > 0 && durationNum <= 1440; // Max 24 hours (1440 minutes)
};

/**
 * Checks if a time on a specific date is in the past based on user's local time
 * @param date - The date for the time (can be Date object or date string)
 * @param time - Time string in HH:MM format
 * @returns true if the datetime is in the past, false otherwise
 */
export const isTimeInPast = (date: Date | string, time: string): boolean => {
  if (!date || !time) return false;

  // Ensure we have a proper Date object
  let dateObj: Date;
  try {
    dateObj = date instanceof Date ? date : new Date(date);
    // Check if the date is valid
    if (isNaN(dateObj.getTime())) {
      return false;
    }
  } catch (error) {
    return false;
  }

  // Get current date and time in user's local timezone
  const now = new Date();
  const [hours, minutes] = time.split(":").map(Number);

  // Create a date object for the selected date with the time slot in user's local timezone
  const timeSlotDate = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate(), hours, minutes, 0, 0);

  // Compare the time slot with current user's local time
  return timeSlotDate < now;
};

/**
 * Gets user-friendly validation error messages
 */
export const getValidationErrorMessages = (t: Translate = fallbackT): ValidationErrorMessages => ({
  eventName: t("validation.title", "Please enter a session title"),
  eventDate: t("validation.date", "Please select an event date"),
  startTime: t("validation.startTime", "Please select a start time"),
  duration: t("validation.duration", "Please select a duration"),
  frequency: t("validation.frequency", "Please select how often this session repeats"),
});

/**
 * Validates session data and returns errors object.
 * In edit mode past dates/times are allowed — an existing Session (or a whole
 * series) legitimately starts in the past; only new Sessions must be upcoming.
 * @param session - Session data to validate
 * @param isEditMode - Whether the form is in edit mode
 * @param t - Translation function (optional; falls back to English)
 * @returns Object containing validation errors
 */
export const validateSession = (
  session: Session | undefined,
  isEditMode: boolean,
  t: Translate = fallbackT
): FormErrors => {
  const newErrors: FormErrors = {};
  const errorMessages = getValidationErrorMessages(t);

  if (!session) {
    newErrors.eventDate = errorMessages.eventDate!;
    newErrors.startTime = errorMessages.startTime!;
    newErrors.duration = errorMessages.duration!;
    return newErrors;
  }

  // Title is auto-generated at submit time when blank — see deriveSessionTitle.

  // Event date is required by backend
  if (!session.dateStart) {
    newErrors.eventDate = errorMessages.eventDate!;
  } else if (!isEditMode) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(session.dateStart);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      newErrors.eventDate = t(
        "validation.dateInPast",
        "Please select a date that is today or in the future"
      );
    }
  }

  // Start time is required by backend and must be valid format
  if (!session.timeLocal) {
    newErrors.startTime = errorMessages.startTime!;
  } else if (!isValidTimeFormat(session.timeLocal)) {
    newErrors.startTime = t(
      "validation.timeFormat",
      "Please enter a valid time in HH:MM or HH:MM:SS format (e.g., 14:30 or 14:30:00)"
    );
  } else if (
    !isEditMode &&
    session.dateStart &&
    isTimeInPast(session.dateStart, session.timeLocal)
  ) {
    newErrors.startTime = t("validation.timeInPast", "Session start time cannot be in the past");
  }

  // Duration is required by backend and must be a valid positive integer
  if (!session.duration || session.duration <= 0) {
    newErrors.duration = errorMessages.duration!;
  } else if (!isValidDuration(session.duration)) {
    newErrors.duration = t(
      "validation.durationRange",
      "Please enter a valid duration between 1 and 1440 minutes (24 hours)"
    );
  }

  // Frequency validation - ready for when default value is removed
  const frequency = mapRecurrenceTypeToFrequency(session.recurrenceType);
  if (!isEditMode && !frequency) {
    newErrors.frequency = errorMessages.frequency!;
  }

  return newErrors;
};
